import { GoogleMap } from '@react-google-maps/api';
import clsx from 'clsx';
import SatelliteGridOverlay from 'components/structure-page/satellite-grid-overlay';
import {
  SatelliteMapPointerSession,
  SatelliteMapStatus
} from 'components/structure-page/satellite-map-picker-types';
import { PointerEvent, useEffect, useMemo, useRef, useState } from 'react';
import { rotationCoverScale } from 'utils/satellite-heading';
import {
  panMapByPointerDelta,
  zoomMapByWheelDelta
} from 'utils/satellite-map-pan';

type SatelliteMapPickerProps = Readonly<{
  center: google.maps.LatLngLiteral;
  zoom: number;
  heading?: number;
  onMapReady: (map: google.maps.Map) => void;
}>;

const MAP_OPTIONS: google.maps.MapOptions = {
  mapTypeId: 'satellite',
  tilt: 0,
  gestureHandling: 'none',
  draggable: false,
  keyboardShortcuts: false,
  zoomControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  mapTypeControl: false,
  rotateControl: false,
  disableDoubleClickZoom: true
};

export default function SatelliteMapPicker({
  center,
  zoom,
  heading = 0,
  onMapReady
}: SatelliteMapPickerProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const pointerSessionRef = useRef<SatelliteMapPointerSession | null>(null);
  const [status, setStatus] = useState<SatelliteMapStatus>('loading');
  const [isDragging, setIsDragging] = useState(false);
  const coverScale = rotationCoverScale(heading);
  const mapContainerStyle = useMemo(
    () => ({
      width: '100%',
      height: '100%',
      transform: `rotate(${heading}deg) scale(${coverScale})`
    }),
    [coverScale, heading]
  );

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node) {
      return undefined;
    }

    const onWheel = (event: WheelEvent) => {
      const map = mapRef.current;
      if (!map) {
        return;
      }
      event.preventDefault();
      zoomMapByWheelDelta(map, event.deltaY);
    };

    node.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      node.removeEventListener('wheel', onWheel);
    };
  }, []);

  const handleMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    onMapReady(map);
    setStatus('ready');
  };

  const handleMapUnmount = () => {
    mapRef.current = null;
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (status !== 'ready' || event.button !== 0) {
      return;
    }
    pointerSessionRef.current = {
      pointerId: event.pointerId,
      lastX: event.clientX,
      lastY: event.clientY
    };
    wrapperRef.current?.setPointerCapture(event.pointerId);
    setIsDragging(true);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const session = pointerSessionRef.current;
    if (!session || session.pointerId !== event.pointerId) {
      return;
    }
    const dx = event.clientX - session.lastX;
    const dy = event.clientY - session.lastY;
    session.lastX = event.clientX;
    session.lastY = event.clientY;
    const map = mapRef.current;
    if (map) {
      panMapByPointerDelta(map, dx, dy, heading);
    }
  };

  const handlePointerEnd = (event: PointerEvent<HTMLDivElement>) => {
    const session = pointerSessionRef.current;
    if (!session || session.pointerId !== event.pointerId) {
      return;
    }
    pointerSessionRef.current = null;
    setIsDragging(false);
    if (wrapperRef.current?.hasPointerCapture(event.pointerId)) {
      wrapperRef.current.releasePointerCapture(event.pointerId);
    }
  };

  return (
    <div
      ref={wrapperRef}
      className={clsx(
        'relative mx-auto aspect-square w-full max-w-[640px] touch-none overflow-hidden rounded-lg border border-gray-200',
        status === 'ready' && (isDragging ? 'cursor-grabbing' : 'cursor-grab')
      )}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
    >
      <GoogleMap
        mapContainerClassName='pointer-events-none absolute inset-0 h-full w-full origin-center'
        mapContainerStyle={mapContainerStyle}
        options={MAP_OPTIONS}
        center={center}
        zoom={zoom}
        onLoad={handleMapLoad}
        onUnmount={handleMapUnmount}
      />
      {status !== 'ready' && (
        <div className='pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-gray-100 text-sm text-gray-500'>
          {status === 'unavailable'
            ? 'Map could not be loaded.'
            : 'Loading map...'}
        </div>
      )}
      {status === 'ready' && (
        <>
          <SatelliteGridOverlay />
          <div className='pointer-events-none absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2'>
            <div className='relative h-8 w-8'>
              <div className='absolute left-1/2 top-0 h-full w-0.5 -translate-x-1/2 bg-red-500/80' />
              <div className='absolute left-0 top-1/2 h-0.5 w-full -translate-y-1/2 bg-red-500/80' />
              <div className='absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-red-500/80' />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
